<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <i class="fas fa-list"></i> {{ $t('page.title.games_results') }}
      <game-result-details-modal ref="game-result-details-modal"></game-result-details-modal>
    </page-header>
    <page-card>
      <template v-slot:header>
        {{ $t('page.filters')}}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :default-filter="$route.query.gameId"
                  filter-operator="like"
                  filter-name="gameId"
                  :label="$t('filters.label.game_id')"
                  :placeholder="$t('filters.placeholder.game_id')">
              </filter-input>
            </div>

            <div class="col-4">
              <filter-input-with-operator
                  :filter-query="filterQuery"
                  :operator-options="filterOptions.comparisonOperator"
                  :operator-multiple="false"
                  :default-operator="{value: 'eq', text: $t('operator.equales')}"
                  :restrict-input="true"
                  restriction-type="decimals"
                  filter-name="buyIn"
                  :label="$t('filters.label.buy_in')"
                  :operator-placeholder="$t('filters.operator_placeholder.buy_in')"
              >
              </filter-input-with-operator>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.status"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="status"
                  :label="$t('filters.label.status')"
                  :placeholder="$t('filters.placeholder.status')">
              </filter-select>
            </div>
          </div>

          <div class="row">
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="walletCurrency"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="currency"
                  :label="$t('filters.label.wallet_currency')"
                  :placeholder="$t('filters.placeholder.wallet_currency')">
              </filter-select>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.maxPlayers"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="numberPlayers"
                  :label="$t('filters.label.max_players')"
                  :placeholder="$t('filters.placeholder.max_players')">
              </filter-select>
            </div>

            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="startedAt"
                  open-direction="left"
                  :label="$t('filters.label.created_at')">
              </filter-date-range-picker>
            </div>
          </div>

          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  filter-name="phrase"
                  :label="$t('filters.label.game_results_phrase')"
                  :placeholder="$t('filters.placeholder.game_results_phrase')">
              </filter-input>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary" class="mr-1">{{$t('filters.btn.clear')}}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{$t('filters.btn.apply')}}</b-button>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.list')}}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
          <div class="table-responsive">
            <b-table striped
                     :busy="isBusy"
                     hover
                     head-variant="dark"
                     outlined
                     :sort-by.sync="orderColumn"
                     :sort-desc.sync="orderDirection"
                     :empty-text="$t('table.no_results')"
                     :no-local-sorting="true"
                     :items="items"
                     :fields="fields">

              <template #cell(gameId)="data">
                {{ data.item.gameId.split('-')[0] }}
              </template>

              <template #cell(startedAt)="data">
                {{ data.item.startedAt | dateFormat('YYYY-MM-DD HH:mm:ss') }}
              </template>

              <template #cell(buyIn)="data">
                {{ data.item.demoGame ? 0 : (data.value | numberFormat) }}
              </template>

              <template #cell(currency)="data">
                {{ data.item.demoGame ? 'Demo' : $t(data.value) }}
              </template>

              <template #cell(status)="data">
                <b-badge pill variant="success" v-if="data.value === 0">{{ $t(data.value) }}</b-badge>
                <b-badge pill variant="warning" v-if="data.value === 1">{{ $t(data.value) }}</b-badge>
                <b-badge pill variant="primary" v-if="data.value === 'CREATED'">{{ $t(data.value) }}</b-badge>
                <b-badge pill variant="warning" v-if="data.value === 'COUNTDOWN_STARTED'">{{ $t(data.value) }}</b-badge>
                <b-badge pill variant="info" v-if="data.value === 'STARTED'">{{ $t(data.value) }}</b-badge>
                <b-badge pill variant="success" v-if="data.value === 'FINISHED'">{{ $t(data.value) }}</b-badge>
                <b-badge pill variant="danger" v-if="data.value === 'CANCELLED'">{{ $t(data.value) }}</b-badge>
                <b-badge pill variant="dark" v-if="data.value === 'UNKNOWN'">{{ $t(data.value) }}</b-badge>
              </template>

              <template #cell(actions)="data">
                <div class="table-action--cell">
                  <b-button variant="success" size="sm" @click="openModal('details', data.item)">{{ $t('table.btn.details') }}</b-button>
                </div>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{$t('state.loading')}}</strong>
                </div>
              </template>
            </b-table>
          </div>
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
        </div>
      </template>
      <template v-slot:footer>

      </template>
    </page-card>

  </div>
</template>

<script>
import GridMixin from "../../components/grid/GridMixin";
import GameResultDetailsModal from "./GameResultDetailsModal";
import FilterSelect from "../../components/grid/filters/FilterSelect";
import FilterInput from "../../components/grid/filters/FilterInput";
import FilterInputWithOperator from "../../components/grid/filters/FilterInputWithOperator";
import FilterDateRangePicker from "../../components/grid/filters/FilterDateRangePicker";
import GridPagination from "../../components/grid/grid-pagination/GridPagination";
import PageHeader from "../../components/page/PageHeader";
import PageCard from "../../components/page/PageCard";
import Vue from "vue";

export default {
  mixins: [GridMixin],
  components: {
    PageCard,
    PageHeader,
    GameResultDetailsModal,
    FilterSelect,
    FilterInput,
    FilterInputWithOperator,
    FilterDateRangePicker,
    GridPagination
  },
  data() {
    return {
      gridGetUrl: '/api/game-history-item/GAME',
      defaultFilters: '&filters[status][0][value]=FINISHED,CANCELLED,UNKNOWN&filters[status][0][operator]=in',
      fields: [
        {key: 'gameId', label: this.$t('table.header.game_id'), sortable: false},
        {key: 'currency', label: this.$t('table.header.currency'), sortable: true},
        {key: 'buyIn', label: this.$t('table.header.buy_in'), sortable: true},
        {key: 'numberPlayers', label: this.$t('table.header.max_players'), sortable: true},
        {key: 'startedAt', label: this.$t('table.header.created_at'), sortable: true},
        {key: 'status', label: this.$t('table.header.status'), sortable: true},
        {key: 'killReason', label: this.$t('table.header.kill_reason'), sortable: true},
        {key: 'actions', label: this.$t('table.header.actions'), sortable: false}
      ],
      perPage: 10,
      currentPage: 1,
      orderColumn: null,
      orderDirection: false,
      filterOptions: {
        status: [
          {value: 'FINISHED', text: this.$t('FINISHED')},
          {value: 'CANCELLED', text: this.$t('CANCELLED')},
          {value: 'UNKNOWN', text: this.$t('UNKNOWN')},
        ],
        walletCurrency: [
          {value: 'FUN_COINS', text: this.$t('FUN_COINS')},
          {value: 'QUIZ_COINS', text: this.$t('QUIZ_COINS')}
        ],
        maxPlayers: [
          {value: '2', text: '2'},
          {value: '3', text: '3'},
          {value: '4', text: '4'}
        ],
        comparisonOperator: [
          {value: 'eq', text: this.$t('operator.equales')},
          {value: 'gte', text: this.$t('operator.greater_then')},
          {value: 'lte', text: this.$t('operator.lower_than')},
          {value: 'btw', text: this.$t('operator.between')},
        ],
      },
      filterQuery: [],
      detailsModalInstance: null,

    };
  },
  computed: {
    walletCurrency: function () {
      return this.$store.getters["root/walletCurrency"].map(d => {
        return {
          value: d.value,
          text: this.$t(d.value)
        }
      });
    },
  },
  methods: {
    storeFilter(data) {
      this.$store.commit('root/setFilter', {storeName: 'players', ...data})
    },
    resetFilters() {
      this.filterQuery = [];
      this.$root.$emit('resetFilters');
      // this.initFilters();
      this.applyFilters();
    },
    openModal(modalName, data) {
      let ModalConstructor;
      switch(modalName){
        case('details'): {
          if(!this.detailsModalInstance){
            ModalConstructor = Vue.extend(GameResultDetailsModal)
            this.detailsModalInstance = new ModalConstructor({parent: this});
            this.detailsModalInstance.$mount();
          }
          this.detailsModalInstance.showDetails(data);
          break;
        }
      }
    }
  }
}
</script>
